<template>
    <div class="w-full">
        <ul class="flex items-center lg:justify-center relative max-w-full overflow-auto max-lg:px-8">
            <li
                v-for="(item, index) in items"
                class="px-8 py-3 relative cursor-pointer"
                :class="index === activeIndex ? 'active text-white font-bold z-10' : 'z-0'"
                @click="handleChangeTab(index)"
            >
                <div class="absolute inset-0 pointer-events-none trapezoid"></div>
                <div class="relative z-10 flex items-center gap-3 select-none" :class="titleSize(size)">
                    <UIcon v-if="item.icon" :name="item.icon" class="w-6 h-6" />
                    <UiComponentTypography class="whitespace-nowrap">
                        {{ item.label }}
                    </UiComponentTypography>
                </div>
            </li>
        </ul>
        <div
            class="bg-white p-4 relative -mt-1 z-10 border-t-4 border-amazon-500"
            ref="containerBox"
            :style="containerBoxStyle"
        >
            <div
                v-if="fixedTabs"
                class="z-40 w-full left-0"
                :class="
                    type === 'scroll' ? 'sticky top-6 lg:top-14' : 'absolute top-[calc(var(--scrollFromTop,0px)+1rem)]'
                "
            >
                <ul class="flex items-center justify-center bg-white shadow-79 rounded-lg">
                    <li
                        v-for="(item, index) in items"
                        class="max-lg:flex-1 max-lg:flex max-lg:justify-center p-2 lg:px-8 lg:py-3 relative cursor-pointer"
                        :class="
                            index === activeIndex
                                ? 'active text-amazon-500 font-bold z-10 border-b-2 border-amazon-500'
                                : 'text-gray-500 hover:text-gray-700 opacity-75 z-0'
                        "
                        @click="handleChangeTab(index)"
                    >
                        <div class="relative z-10 flex items-center gap-3 select-none">
                            <UIcon v-if="item.icon" :name="item.icon" class="w-6 h-6" />
                            <UiComponentTypography class-name="max-lg:text-xs whitespace-nowrap max-lg:hidden">
                                {{ item.label }}
                            </UiComponentTypography>
                        </div>
                    </li>
                </ul>
            </div>
            <div v-if="type === 'tabs'">
                <p v-if="typeof items[activeIndex].content === 'string'">
                    {{ items[activeIndex].content }}
                </p>
                <component
                    v-else
                    :is="items[activeIndex].content.component"
                    v-bind="items[activeIndex].content.props"
                />
            </div>
            <div v-else>
                <div v-for="(item, index) in items" :ref="(el) => setElementRef(el, index)">
                    <div class="flex mt-12 mb-6">
                        <div class="flex items-center gap-4 border-b-2 border-amazon-500 py-2 pl-12 md:pl-40">
                            <UIcon v-if="item.icon" :name="item.icon" class="w-6 h-6 text-amazon-500" />
                            <UiComponentTypography tag="h4">
                                {{ item.label }}
                            </UiComponentTypography>
                        </div>
                    </div>
                    <p v-if="typeof item.content === 'string'">
                        {{ item.content }}
                    </p>
                    <component v-else :is="item.content.component" v-bind="item.content.props" />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
interface ITabs {
    label: string;
    icon?: string;
    content?: any;
    props?: any;
}

const emits = defineEmits(["update:tab"]);

const props = defineProps({
    items: {
        type: Array as PropType<ITabs[]>,
        required: true,
    },
    type: {
        type: String as PropType<"tabs" | "scroll">,
        default: "tabs",
    },
    fixedTabs: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String as PropType<"xs" | "sm" | "default" | "md" | "lg" | "xl">,
        default: "default",
    },
});

const activeIndex = ref(0);
const elementRefs: Ref<HTMLElement[]> = ref([]);
const containerBox: Ref<HTMLElement | null> = ref(null);

const { y } = useElementBounding(containerBox);

const containerBoxStyle = computed(() => {
    return {
        "--scrollFromTop": -1 * y.value + "px",
    };
});

const setElementRef = (el: Element | ComponentPublicInstance | null, index: number) => {
    if (el) {
        elementRefs.value[index] = el as HTMLElement;
    }
};

const scrollToElement = (index: number) => {
    const element = elementRefs.value[index];
    if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

function handleChangeTab(index: number) {
    activeIndex.value = index;
    if (props.type === "tabs") emits("update:tab");
    else scrollToElement(index);
}

onMounted(() => {
    elementRefs.value.forEach((el, index) => {
        const { stop } = useIntersectionObserver(
            el,
            ([entry]) => {
                if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
                    activeIndex.value = index;
                }
            },
            {
                threshold: 0.25,
            }
        );

        onBeforeUnmount(() => {
            stop();
        });
    });
});

const titleSize = (size: string) => {
    switch (size) {
        case "xs":
            return "text-xs";
        case "sm":
            return "text-sm";
        case "default":
            return "";
        case "md":
            return "text-md";
        case "lg":
            return "text-lg";
        case "xl":
            return "text-xl";
        default:
            return "";
    }
};
</script>

<style scoped>
.trapezoid {
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.15));
}
.trapezoid::before,
.trapezoid::after {
    content: "";
    @apply absolute h-full w-2/3 bg-white rounded-t-lg;
}
.active .trapezoid::before,
.active .trapezoid::after {
    @apply bg-amazon-500;
}
.trapezoid::after {
    transform: skewX(15deg);
    @apply right-0;
}
.trapezoid::before {
    transform: skewX(-15deg);
    @apply left-0;
}
/* Transition styles */
.v-enter-active {
    transition: all 0.3s ease;
}
.v-leave-active {
    transition: all 0.2s ease;
}
.v-enter-from {
    opacity: 0;
    transform: translateY(-15px);
}
.v-leave-to {
    opacity: 0;
    transform: translateY(15px);
}
</style>
